import React, { useEffect, useRef, useState } from "react";
import { Alert, Empty, Progress, Space, Table, Tag, Typography, Row, Col, Statistic } from "antd";
import dayjs from "dayjs";
import { Card, Badge, Skeleton, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";

const { Text } = Typography;

const usageColor = (pct) =>
  pct >= 90 ? "var(--color-error)" : pct >= 70 ? "var(--color-warning)" : "var(--color-primary)";

const pct = (used, limit) => (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0);

const fmt = (value) => Number(value ?? 0).toLocaleString();

/**
 * One allowance bar. Both cycles get the same treatment because either one can be the thing
 * that stops a request — showing only the monthly bar left clients confused about why a plan
 * reading "40% used" had stopped answering.
 */
const AllowanceBar = ({ label, used, limit, resetUtc }) => {
  const percent = pct(used, limit);
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label} — {fmt(used)} of {fmt(limit)}
        </Text>
        <Text strong style={{ fontSize: 12 }}>
          {percent}%
        </Text>
      </div>
      <Progress percent={percent} strokeColor={usageColor(percent)} showInfo={false} />
      {resetUtc && (
        <Text style={{ fontSize: 11, color: "var(--text-muted)" }}>
          Resets {dayjs(resetUtc).format("DD MMM YYYY")}
        </Text>
      )}
    </div>
  );
};

/** Read-only view of the client's own plan, allowance, credit and billing periods. */
const PortalSubscription = () => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    mutateRef
      .current("portalSubscription", {
        method: "get",
        endpoint: "/portal/subscription",
        onSuccess: (data) => {
          if (!cancelled) setDetail(data);
        },
        onError: () => {
          if (!cancelled) setMissing(true);
        },
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const cycle = detail?.currentCycle;
  const wallet = detail?.wallet;
  const overdraft = detail?.overdraft;

  const periodColumns = [
    {
      title: "Period",
      key: "period",
      render: (_, r) => (
        <Space size={6}>
          <Text style={{ fontSize: 12 }}>
            {dayjs(r.fromDate).format("DD MMM YY")} – {dayjs(r.toDate).format("DD MMM YY")}
          </Text>
          {r.isCurrent && <Badge variant="success">Current</Badge>}
        </Space>
      ),
    },
    { title: "Used", dataIndex: "tokenUsed", key: "tokenUsed", render: fmt },
    { title: "Limit", dataIndex: "tokenLimit", key: "tokenLimit", render: fmt },
    { title: "Remaining", dataIndex: "tokenRemaining", key: "tokenRemaining", render: fmt },
    {
      title: "Top-ups",
      key: "topUps",
      render: (_, r) => (r.topUps?.length ? <Tag color="blue">{r.topUps.length}</Tag> : "—"),
    },
  ];

  // Every top-up across every period, newest first: credit is not tied to the cycle it was
  // bought in, so burying it inside one period's row would misrepresent how it behaves.
  const allTopUps = (detail?.periods ?? [])
    .flatMap((p) => p.topUps ?? [])
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const topUpColumns = [
    {
      title: "Purchased",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => dayjs(v).format("DD MMM YY"),
    },
    {
      title: "Tokens",
      dataIndex: "tokenAllowance",
      key: "tokenAllowance",
      render: (v, r) => (
        <span>
          {fmt(v)}
          {r.units > 1 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {" "}
              ({r.units} packs)
            </Text>
          )}
        </span>
      ),
    },
    { title: "Used", dataIndex: "tokensConsumed", key: "tokensConsumed", render: fmt },
    {
      title: "Remaining",
      dataIndex: "tokensRemaining",
      key: "tokensRemaining",
      render: (v) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: "Expires",
      dataIndex: "expiresAtUtc",
      key: "expiresAtUtc",
      render: (v, r) =>
        r.revokedAtUtc ? (
          <Badge variant="neutral">Refunded</Badge>
        ) : v ? (
          dayjs(v).format("DD MMM YY")
        ) : (
          "—"
        ),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (v, r) => `${Number(v || 0).toFixed(2)} ${r.currencyCode || "EUR"}`,
    },
  ];

  if (loading) return <Skeleton rows={6} />;

  if (missing || !detail) {
    return (
      <Card padding="md">
        <Empty description="No subscription is attached to your account yet. Contact Kubit AI to get started." />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <PageHeader
        title="Subscription"
        subtitle="Your current plan, limits and renewal details."
        className="page-header--flush"
      />

      {/* Money owed comes first: it is the only state here that can stop the service. */}
      {overdraft?.outstanding > 0 && (
        <Alert
          type={overdraft.isOverdue ? "error" : "warning"}
          showIcon
          message={
            overdraft.isOverdue
              ? `Requests are paused — ${fmt(overdraft.outstanding)} tokens to settle`
              : `${fmt(overdraft.outstanding)} tokens used on account`
          }
          description={
            overdraft.isOverdue
              ? `The settlement period ended on ${dayjs(overdraft.dueAtUtc).format(
                  "DD MMM YYYY"
                )}. Buying a top-up clears this balance and resumes your API access immediately.`
              : `You kept working past your allowance. Settle by ${dayjs(overdraft.dueAtUtc).format(
                  "DD MMM YYYY"
                )} — a top-up pays this off first, and anything left over becomes usable credit. You can use up to ${fmt(
                  overdraft.available
                )} more tokens on account before then.`
          }
        />
      )}

      {wallet?.available > 0 && wallet?.nextExpiryUtc && (
        <Alert
          type="info"
          showIcon
          message={`${fmt(wallet.expiringSoon)} credit tokens expire on ${dayjs(
            wallet.nextExpiryUtc
          ).format("DD MMM YYYY")}`}
          description="Credit does not carry over past the billing period it was bought in."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="Plan" value={detail.packageName || "—"} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="Billing period" value={detail.periodType || "—"} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Allowance left this month"
              value={fmt(cycle?.monthlyRemaining ?? 0)}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Top-up credit"
              value={fmt(wallet?.available ?? 0)}
              valueStyle={{
                color: wallet?.available > 0 ? "var(--color-primary)" : undefined,
              }}
            />
          </Card>
        </Col>
      </Row>

      {cycle && (
        <Card title="Plan allowance" padding="md">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
            <AllowanceBar
              label="This week"
              used={cycle.weeklyUsed}
              limit={cycle.weeklyLimit}
              resetUtc={cycle.weekResetUtc}
            />
            <AllowanceBar
              label="This month"
              used={cycle.monthlyUsed}
              limit={cycle.monthlyLimit}
              resetUtc={cycle.monthResetUtc}
            />
          </div>
          <Text style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginTop: 12 }}>
            These bars track your plan allowance only. Top-up credit is spent after the allowance
            runs out and is shown separately, so a full bar does not mean your API has stopped.
            {cycle.walletUsedInCycle > 0 &&
              ` ${fmt(cycle.walletUsedInCycle)} credit tokens have been used this month.`}
          </Text>
        </Card>
      )}

      {allTopUps.length > 0 && (
        <Card title="Top-up credit" padding="sm">
          <Table
            rowKey="paymentId"
            columns={topUpColumns}
            dataSource={allTopUps}
            size="small"
            pagination={{ pageSize: 5, showSizeChanger: false }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>Available</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3}>
                  <Text strong>{fmt(wallet?.available ?? 0)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} />
              </Table.Summary.Row>
            )}
          />
        </Card>
      )}

      <Card title="Billing periods" padding="sm">
        {detail.periods?.length ? (
          <Table
            rowKey="paymentId"
            columns={periodColumns}
            dataSource={detail.periods}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        ) : (
          <Empty description="No billing periods yet" />
        )}
      </Card>
    </Space>
  );
};

export default PortalSubscription;
